import {useMemo} from 'react'
import {Pressable} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {type Interest} from '#/lib/interests'
import {DraggableScrollView} from '#/view/com/pager/DraggableScrollView'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {useTrendLabels} from '../data/labels'

/**
 * Trend categories returned by `GET /trends`. These are aligned 1:1 with the
 * user-interest taxonomy (`src/lib/interests.ts`) — the backend only emits
 * categories that are also selectable interests, so a trend's `category` is
 * always a valid `Interest` slug. This lets the "All" chip filter trends down
 * to the viewer's own interests (see `filterTrendsResponseByInterests`).
 */
export type TrendsCategory = Interest

/**
 * Categories present in `trends-mock-data.json`. Only used as the default
 * `categories` prop when the parent doesn't pass a live list (it derives the
 * real list from the current trends response).
 */
export const MOCK_TRENDS_CATEGORIES: TrendsCategory[] = [
  'politics',
  'tech',
  'culture',
  'sports',
]

/**
 * Horizontal chip row rendered directly below `TrendsScopeTabs` on `/search`.
 *
 * Mirrors the categories frame in Figma file `yYh1MBotDu5Gm1KpL8EZxH`
 * (W Social — Product — Trends), node `807:5944`.
 *
 * `selected === null` represents the "All" chip (no filter applied).
 */
export function TrendsCategoryChips({
  selected,
  onSelect,
  categories = MOCK_TRENDS_CATEGORIES,
  interests = [],
}: {
  selected: TrendsCategory | null
  onSelect: (category: TrendsCategory | null) => void
  categories?: TrendsCategory[]
  /** The viewer's selected interests — these categories sort first. */
  interests?: readonly string[]
}) {
  const {_} = useLingui()
  const ss = useSemanticTokens()
  // Single source of truth for category labels lives in `useTrendLabels` so the
  // chips and the cards never drift.
  const {getCategoryLabel} = useTrendLabels()

  // Order: categories matching the viewer's interests first (A→Z by localized
  // label), then the rest (A→Z). "All" is rendered separately and stays first.
  const sortedCategories = useMemo(() => {
    const interestSet = new Set(interests)
    return [...categories].sort((catA, catB) => {
      const aMatch = interestSet.has(catA)
      const bMatch = interestSet.has(catB)
      if (aMatch !== bMatch) return aMatch ? -1 : 1
      return getCategoryLabel(catA).localeCompare(getCategoryLabel(catB))
    })
  }, [categories, interests, getCategoryLabel])

  return (
    <DraggableScrollView
      showsHorizontalScrollIndicator={false}
      // Border lives on the ScrollView frame (fixed, viewport-width), NOT the
      // content container — the content layer scrolls and is wider than the
      // screen, so a border there cuts off / shifts when scrolling.
      style={[a.border_b, {borderColor: ss.border.contrast_low}]}
      contentContainerStyle={[a.flex_row, a.gap_sm, a.px_lg, a.py_lg]}>
      <CategoryChip
        label={_(msg`All`)}
        isActive={selected === null}
        onPress={() => onSelect(null)}
      />
      {sortedCategories.map(category => (
        <CategoryChip
          key={category}
          label={getCategoryLabel(category)}
          isActive={selected === category}
          onPress={() => onSelect(category)}
        />
      ))}
    </DraggableScrollView>
  )
}

function CategoryChip({
  label,
  isActive,
  onPress,
}: {
  label: string
  isActive: boolean
  onPress: () => void
}) {
  const t = useTheme()
  const ss = useSemanticTokens()

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{selected: isActive}}
      onPress={onPress}
      style={[
        a.align_center,
        a.justify_center,
        a.px_lg,
        a.rounded_full,
        {
          height: 34,
          backgroundColor: isActive ? t.atoms.text.color : ss.bg.default,
        },
      ]}>
      <Text
        style={[
          a.text_sm,
          a.font_bold,
          isActive ? t.atoms.text_inverted : t.atoms.text_contrast_medium,
        ]}>
        {label}
      </Text>
    </Pressable>
  )
}
